/**
 * Seletor de empresa para o header
 * Superadmins e staff: dropdown com todas as empresas
 * Usuarios com multiplas empresas: dropdown com suas empresas
 * Usuarios com 1 empresa: read-only
 */
import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { BuildingOfficeIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import useAuthStore from '../../stores/authStore';
import { useCompany } from '../../contexts/CompanyContext';

const CompanySelector: React.FC = () => {
  const { user, isLoading: authLoading } = useAuthStore();
  const { currentCompany, companies, isLoading: companiesLoading, switchCompany } = useCompany();

  if (authLoading || companiesLoading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 rounded-lg animate-pulse">
        <div className="h-4 w-4 bg-gray-300 rounded"></div>
        <div className="h-4 w-24 bg-gray-300 rounded"></div>
      </div>
    );
  }

  const isSuperOrStaff = user?.is_superuser || user?.is_staff;
  const hasMultipleCompanies = companies.length > 1;
  const showDropdown = isSuperOrStaff || hasMultipleCompanies;

  // Nome e estilo a exibir
  const displayName = currentCompany?.name || (isSuperOrStaff ? 'Ezra Admin' : user?.company_name || 'Sem empresa');
  const isAdmin = isSuperOrStaff && !currentCompany;

  // Sem dropdown: usuario com 1 empresa ou sem empresa
  if (!showDropdown) {
    if (!user?.company_name && !currentCompany) {
      if (isSuperOrStaff) {
        return (
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg">
            <BuildingOfficeIcon className="h-4 w-4" />
            <span className="text-sm font-medium">Ezra Admin</span>
          </div>
        );
      }
      return (
        <div className="flex items-center space-x-2 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg">
          <BuildingOfficeIcon className="h-4 w-4" />
          <span className="text-sm">Sem empresa</span>
        </div>
      );
    }

    return (
      <div
        className="flex items-center space-x-2 px-3 py-1.5 rounded-lg"
        style={{ border: '1px solid var(--color-border-default)' }}
      >
        <BuildingOfficeIcon className="h-4 w-4" style={{ color: 'var(--color-text-secondary)' }} />
        <span className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
          {displayName}
        </span>
      </div>
    );
  }

  // Dropdown interativo
  const handleSwitch = async (companyId: string) => {
    try {
      await switchCompany(companyId);
    } catch (err) {
      console.error('Erro ao trocar empresa:', err);
    }
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => (
        <>
          <Menu.Button
            className={`
              flex items-center space-x-2 px-3 py-1.5 rounded-lg
              transition-all focus:outline-none focus:ring-2 focus:ring-offset-2
              ${isAdmin
                ? 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                : 'text-gray-900 hover:border-[color:var(--color-brand-accent)]'
              }
            `}
            style={isAdmin ? {} : { border: '1px solid var(--color-border-default)' }}
          >
            <BuildingOfficeIcon className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm font-medium truncate max-w-[160px]">
              {displayName}
            </span>
            <ChevronDownIcon
              className={`h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ${
                open ? 'rotate-180' : ''
              }`}
            />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-50 mt-2 w-64 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                <div className="px-4 py-2 border-b border-gray-200">
                  <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                    Trocar empresa
                  </p>
                </div>

                <div className="max-h-60 overflow-y-auto py-1">
                  {companies.map((company) => (
                    <Menu.Item key={company.id}>
                      {({ active }) => (
                        <button
                          onClick={() => handleSwitch(company.id)}
                          className={`
                            flex items-center w-full px-4 py-2 text-sm
                            ${active ? 'bg-gray-100' : ''}
                            ${currentCompany?.id === company.id ? 'text-primary-700 font-medium' : 'text-gray-700'}
                          `}
                        >
                          <BuildingOfficeIcon className="h-4 w-4 mr-3 flex-shrink-0 text-gray-400" />
                          <span className="truncate flex-1 text-left">{company.name}</span>
                          {currentCompany?.id === company.id && (
                            <CheckIcon className="h-4 w-4 ml-2 flex-shrink-0 text-primary-600" />
                          )}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default CompanySelector;
